import React, {useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import {jsPDF} from 'jspdf';
import SafeIcon from '../common/SafeIcon';

const {FiDownload, FiAlertCircle, FiCheck} = FiIcons;

const DownloadPDF = ({profitData, formData, onDownload, currentUser, onLoginRequest}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const generatePDF = async () => {
    setLoading(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const logoUrl = "https://ik.imagekit.io/cy1p3cqmc/endpoint/images/Logo/local-Trust-Guide-Logo.png";
      
      const img = new Image();
      img.src = logoUrl;
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      pdf.addImage(img, 'PNG', 20, 10, 40, 20);
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text("Local Trust Guide", 70, 20);
      
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text("We help restaurants grow without depending on ads.", 70, 28);

      pdf.setFontSize(14);
      pdf.setTextColor(0, 0, 0);
      pdf.text("Restaurant Profit Analysis Report", 20, 45);

      pdf.setFontSize(12);
      pdf.text(`Restaurant: ${currentUser?.restaurantName || 'Valued Partner'}`, 20, 60);
      pdf.text(`Owner: ${currentUser?.name || 'Owner'}`, 20, 70);
      pdf.text(`Date: ${new Date().toLocaleDateString()}`, 20, 80);

      pdf.setFontSize(14);
      pdf.setTextColor(0, 51, 102);
      pdf.text('Profit Analysis', 20, 100);
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Daily Profit: RM${profitData.daily.toFixed(2)}`, 20, 115);
      pdf.text(`Weekly Profit: RM${profitData.weekly.toFixed(2)}`, 20, 125);
      pdf.text(`Monthly Profit: RM${profitData.monthly.toFixed(2)}`, 20, 135);
      pdf.text(`Profit Margin: ${profitData.margin.toFixed(1)}%`, 20, 145);

      pdf.setFontSize(14);
      pdf.setTextColor(0, 51, 102);
      pdf.text('Cost Breakdown', 20, 165);
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Food & Beverage: RM${formData.foodCosts || '0'}`, 20, 180);
      pdf.text(`Labor: RM${formData.laborCosts || '0'}`, 20, 190);
      pdf.text(`Rent & Utilities: RM${formData.rentUtilities || '0'}`, 20, 200);
      pdf.text(`Other Expenses: RM${formData.otherExpenses || '0'}`, 20, 210);

      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text('© 2024 Local Trust Guide. We help restaurants grow without depending on ads.', 20, 270);

      const filename = `${(currentUser?.restaurantName || 'Restaurant').replace(/\s+/g, '_')}_profit_analysis.pdf`;
      pdf.save(filename);

      if (onDownload) {
        onDownload({
          id: Date.now(),
          restaurant: currentUser?.restaurantName || 'Unknown',
          owner: currentUser?.name || 'Unknown',
          email: currentUser?.email || 'N/A',
          timestamp: new Date().toLocaleString(),
          revenue: `RM${formData.revenue || '0'}`,
          profit: `RM${profitData.daily.toFixed(2)}`,
          margin: `${profitData.margin.toFixed(1)}%`
        });
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error(err);
      setError('Failed to generate PDF. Please try again.');
    }
    setLoading(false);
  };

  const handleDownload = (e) => {
    e.preventDefault();
    if (!currentUser) {
      onLoginRequest();
      return;
    }
    generatePDF();
  };

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      transition={{duration: 0.6, delay: 0.8}}
      className="bg-white rounded-3xl shadow-xl p-8 border border-slate-200"
    >
      <div className="text-center">
        <motion.button
          whileHover={{scale: 1.05}}
          whileTap={{scale: 0.95}}
          onClick={handleDownload}
          disabled={loading}
          className={`w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          <div className="flex items-center justify-center gap-2">
            <SafeIcon icon={loading ? FiAlertCircle : success ? FiCheck : FiDownload} className="text-xl" />
            <span>
              {loading ? 'Generating PDF...' : success ? 'Download Complete!' : 'Download Profit Report'}
            </span>
          </div>
        </motion.button>
        {error && (
          <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-xl border border-red-200">
            <p className="text-sm">{error}</p>
          </div>
        )}
        <p className="mt-4 text-sm text-slate-500">
          Download a detailed PDF report of your restaurant's profit analysis
        </p>
      </div>
    </motion.div>
  );
};

export default DownloadPDF;